import React, { useState, useCallback } from 'react';
import { Play, Pause, Download, Trash2, RotateCcw } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Spinner from '../Spinner';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';
import useIsMobile from '../../hooks/useIsMobile';
import TrackWaveform from './TrackWaveform';
import AudioAssetsSkeleton from '../skeletons/AudioAssetsSkeleton';
import { C } from '../../styles/tokens';

export interface AudioAsset {
  id: string;
  integration_id: string;
  prompt: string;
  audio_url: string;
  duration: number;
  file_size: number;
  category?: string;
  tags?: string[];
  is_public?: boolean;
  is_featured?: boolean;
  status: string;
  created_at: string;
  updated_at: string;
}

interface AudioAssetsListProps {
  assets: AudioAsset[];
  isLoading: boolean;
  isFetching?: boolean; // For showing loading state during pagination
  error: any;
  showDelete?: boolean;
  emptyStateConfig: {
    // Icon is optional — the empty state is now a plain-text treatment with
    // no icon/gradient circle, matching the reference design.
    icon?: React.ReactNode;
    title: string;
    description: string;
  };
  onDelete?: (id: string) => Promise<void>;
  onDownload?: (asset: AudioAsset) => void;
  // Repopulate the (lifted) prompt value with this asset's prompt so the user
  // can tweak and regenerate it. Purely client-side — no new API call.
  onRegenerate?: (prompt: string) => void;
  // Selectable mode props (used in editor modals)
  selectable?: boolean;
  selectedId?: string | null;
  onSelect?: (asset: AudioAsset) => void;
  compact?: boolean; // Hides date & action columns
}

// Helper function to get the full audio URL
const getAudioURL = (audioPath: string) => {
  // If it's already a full URL, return as is
  if (audioPath.startsWith('http://') || audioPath.startsWith('https://')) {
    return audioPath;
  }
  // Otherwise, construct the URL from the path
  return `${process.env.REACT_APP_AUDIO_PREVIEW_URL}/${audioPath}`;
};

// Deterministic pseudo-random gradients used to fill a row's thumbnail —
// seeded off the asset id/prompt so the same asset always looks the same.
const THUMB_GRADIENTS = [
  'linear-gradient(135deg,#F59E0B,#EF4444)',
  'linear-gradient(135deg,#0EA5E9,#6366F1)',
  'linear-gradient(135deg,#8B5CF6,#EC4899)',
  'linear-gradient(135deg,#10B981,#0EA5E9)',
  'linear-gradient(135deg,#F97316,#FBBF24)',
  'linear-gradient(135deg,#78716C,#A8A29E)',
  'linear-gradient(135deg,#DC2626,#F59E0B)',
  'linear-gradient(135deg,#16A34A,#84CC16)',
  'linear-gradient(135deg,#7C3AED,#A78BFA)',
  'linear-gradient(135deg,#0F172A,#334155)',
];

function hashToGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return THUMB_GRADIENTS[hash % THUMB_GRADIENTS.length];
}

// "Xm Ys" duration formatting (e.g. "1m 4s") instead of raw seconds.
function formatDuration(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = Math.round(safeSeconds % 60);
  return `${minutes}m ${seconds}s`;
}

const iconBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  flexShrink: 0,
};

const AudioAssetsList: React.FC<AudioAssetsListProps> = ({
  assets,
  isLoading,
  isFetching = false,
  error,
  showDelete = false,
  emptyStateConfig,
  onDelete,
  onDownload,
  onRegenerate,
  selectable = false,
  selectedId = null,
  onSelect,
  compact = false
}) => {
  const isMobile = useIsMobile();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { play: playAudio, toggle: toggleAudio, isPlaying } = useAudioPlayer(() => {
    setPlayingId(null);
  });

  const handlePlayPause = useCallback((asset: AudioAsset) => {
    if (playingId === asset.id) {
      toggleAudio();
    } else {
      playAudio(getAudioURL(asset.audio_url));
      setPlayingId(asset.id);
    }
  }, [playingId, toggleAudio, playAudio]);

  const handleDownload = async (asset: AudioAsset) => {
    if (onDownload) { onDownload(asset); return; }
    setDownloadingId(asset.id);
    try {
      const filename = `${asset.prompt.substring(0, 50).replace(/[^a-z0-9]/gi, '_')}.wav`;
      await downloadFile(getAudioURL(asset.audio_url), filename);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!onDelete) return;

    if (!window.confirm('Are you sure you want to delete this audio asset?')) {
      return;
    }

    await onDelete(id);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const stateMinHeight = compact ? '200px' : '400px';

  if (isLoading) {
    return <AudioAssetsSkeleton compact={compact} />;
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: stateMinHeight }}>
        <div className="text-center">
          <p style={{ fontSize: '16px', color: '#e53e3e' }}>
            Failed to load audio assets. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (assets.length === 0) {
    // Plain, icon-free empty state — matches the reference design exactly.
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: stateMinHeight }}>
        <div className="text-center" style={{ maxWidth: '400px' }}>
          <h3 className="mb-2" style={{ fontSize: 20, fontWeight: 700, color: C.n900 }}>
            {emptyStateConfig.title}
          </h3>
          <p style={{ fontSize: 14, color: C.n400 }}>
            {emptyStateConfig.description}
          </p>
        </div>
      </div>
    );
  }

  const desktopCols = compact ? '1fr 200px 70px' : '1fr 200px 70px 90px 116px';

  return (
    <div style={{ position: 'relative' }}>
      {/* Loading Overlay for pagination (only show when fetching but not initial loading) */}
      {isFetching && !isLoading && (
        <div
          className="position-absolute d-flex justify-content-center align-items-center"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10
          }}
        >
          <Spinner color="primary" />
        </div>
      )}

      {/* Header row */}
      {!isMobile && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: desktopCols,
            gap: 12,
            padding: '6px 12px',
            color: C.n400,
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          <div>Track</div>
          <div />
          <div>Duration</div>
          {!compact && <div>Created</div>}
          {!compact && <div />}
        </div>
      )}

      {/* Rows */}
      {assets.map((asset) => {
        const isSelected = selectable && selectedId === asset.id;
        const isRowPlaying = playingId === asset.id && isPlaying;
        return (
          <div
            key={asset.id}
            onClick={selectable ? () => onSelect?.(asset) : undefined}
            onMouseEnter={() => setHoveredRowId(asset.id)}
            onMouseLeave={() => setHoveredRowId(null)}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr auto' : desktopCols,
              gap: 12,
              alignItems: 'center',
              padding: isMobile ? '10px 8px' : '9px 12px',
              borderTop: `1px solid ${C.n100}`,
              borderLeft: isSelected ? `3px solid ${C.n900}` : '3px solid transparent',
              background: isSelected ? C.n50 : hoveredRowId === asset.id ? C.n50 : C.w,
              cursor: selectable ? 'pointer' : 'default',
              transition: 'background-color .15s ease, border-color .15s ease',
            }}
          >
            {/* Thumbnail + title/tags */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
              <button
                onClick={(e) => { e.stopPropagation(); handlePlayPause(asset); }}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9,
                  flexShrink: 0,
                  border: 'none',
                  cursor: 'pointer',
                  background: hashToGradient(asset.id || asset.prompt),
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.28)', borderRadius: 9 }} />
                {isRowPlaying ? (
                  <Pause size={14} color="#fff" fill="#fff" style={{ position: 'relative' }} />
                ) : (
                  <Play size={14} color="#fff" fill="#fff" style={{ position: 'relative' }} />
                )}
              </button>
              <div style={{ minWidth: 0 }}>
                <OverlayTrigger placement="top" overlay={<Tooltip>{asset.prompt}</Tooltip>}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: C.n900,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {asset.prompt}
                  </div>
                </OverlayTrigger>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                  {asset.category && (
                    <span style={{ fontSize: 11.5, color: C.n500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {asset.category}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Waveform (decorative) */}
            {!isMobile && (
              <div style={{ minWidth: 0 }}>
                <TrackWaveform seed={asset.id || asset.prompt} />
              </div>
            )}

            {/* Duration */}
            {!isMobile && (
              <div style={{ fontSize: 12.5, color: C.n600, fontVariantNumeric: 'tabular-nums' }}>
                {formatDuration(asset.duration)}
              </div>
            )}

            {/* Created date */}
            {!isMobile && !compact && (
              <div style={{ fontSize: 12, color: C.n500 }}>{formatDate(asset.created_at)}</div>
            )}

            {/* Actions */}
            {!compact && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                {onRegenerate && (
                  <OverlayTrigger placement="top" overlay={<Tooltip>Regenerate</Tooltip>}>
                    <button
                      style={iconBtnStyle}
                      onClick={(e) => { e.stopPropagation(); onRegenerate(asset.prompt); }}
                    >
                      <RotateCcw size={14} color={C.n500} />
                    </button>
                  </OverlayTrigger>
                )}

                <OverlayTrigger
                  placement="top"
                  delay={{ show: 300, hide: 0 }}
                  overlay={<Tooltip>Download</Tooltip>}
                  show={downloadingId === asset.id ? false : undefined}
                >
                  <button
                    style={iconBtnStyle}
                    disabled={downloadingId === asset.id}
                    onClick={(e) => { e.stopPropagation(); handleDownload(asset); }}
                  >
                    {downloadingId === asset.id
                      ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                      : <Download size={14} color={C.n500} />
                    }
                  </button>
                </OverlayTrigger>

                {showDelete && onDelete && (
                  <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                    <button
                      style={iconBtnStyle}
                      onClick={(e) => { e.stopPropagation(); handleDelete(asset.id); }}
                    >
                      <Trash2 size={14} color={C.r500} />
                    </button>
                  </OverlayTrigger>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AudioAssetsList;
